import React, {
  useRef,
  ChangeEvent,
  Dispatch,
  useState,
  useCallback,
} from 'react';

import { TFunction } from 'next-i18next';
import { ActionType } from '@/features/products/list/reducer';
import { buildDropdownOptions } from '@/features/products/list/util';
import {
  Input,
  SearchIcon,
  Dropdown,
  Button,
  Icon,
  FilterIcon,
  Modal,
  ModalHeader,
  XLineIcon,
  ModalBody,
  ModalFooter,
  FilterIconCustom,
} from '@/components';
import {
  ReducerTypes,
  Category,
  InitMasterDataTypes,
  ReducerType,
  priceFilter,
  InitMasterDataCustomTypes,
} from '@/features/products/list/const';
import { useRouter } from 'next/router';
import { AnimatePresence } from 'framer-motion';
import { debounce } from '@/libs/app';

type ListFilterPartialProps = {
  state: ReducerTypes;
  categories: Category[];
  dispatch: Dispatch<ActionType>;
  masterData: InitMasterDataTypes;
  masterDataPearl: InitMasterDataCustomTypes;
  t: TFunction;
};

const FilterPartial = ({
  state,
  dispatch,
  masterDataPearl,
  masterData,
  t,
}: ListFilterPartialProps) => {
  const Router = useRouter();
  const [isShow, setShow] = useState<boolean>(false);
  const onKeywordChange = (value: string) => {
    dispatch({ type: ReducerType.OnPageChange, payload: 1 });
    if (value.includes('#')) {
      dispatch({
        type: ReducerType.OnTagChange,
        payload: value.replace('#', ''),
      });
    } else dispatch({ type: ReducerType.OnKeywordChange, payload: value });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const optimizedFn = useCallback(debounce(onKeywordChange, 300), []);

  const sortByOptions = [
    {
      text: t('list.filter.date-dsc'),
      value: 'created_at|desc',
    },
    {
      text: t('list.filter.price-asc'),
      value: 'price|asc',
    },
    {
      text: t('list.filter.price-desc'),
      value: 'price|desc',
    },
  ];

  const resetPage = () => {
    Router.push(
      { pathname: Router.pathname, query: { ...Router.query, page: 1 } },
      undefined,
      {
        shallow: true,
      }
    );
    dispatch({ type: ReducerType.OnPageChange, payload: 1 });
  };

  const onGemTypeChange = (value: string) => {
    resetPage();
    return dispatch({ type: ReducerType.OnGemTypeChange, payload: value });
  };

  const onGemPureChange = (value: string) => {
    resetPage();
    return dispatch({ type: ReducerType.OnGemPureChange, payload: value });
  };

  const onColorChange = (value: string) => {
    resetPage();

    return dispatch({ type: ReducerType.OnColorChange, payload: value });
  };

  const onGemMaterialChange = (value: string) => {
    resetPage();

    return dispatch({ type: ReducerType.OnGemMaterialChange, payload: value });
  };

  const onPriceChange = (value: string) => {
    resetPage();
    return dispatch({ type: ReducerType.OnPriceChange, payload: value });
  };

  const onJewelryMaterialChange = (value: string) => {
    resetPage();
    return dispatch({
      type: ReducerType.OnJewelryMaterialChange,
      payload: value,
    });
  };

  const onChangeSort = (e: any) =>
    dispatch({ type: ReducerType.OnSortBy, payload: e });

  return (
    <>
      <div className="md:tw-flex tw-flex-col tw-gap-y-6 tw-hidden">
        {/* <h4 className="tw-uppercase tw-font-medium tw-text-secondary-70 tw-text-base tw-hidden sm:tw-block">
          {t('list.label.filter')}
        </h4> */}

        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-12">
          <div className="md:tw-col-span-8">
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-2">
              <Dropdown
                value={state.gem_material}
                onChange={onGemMaterialChange}
                className="tw-border tw-rounded-5 custom-border-v2 md:tw-w-full tw-py-1 tw-px-2"
                options={buildDropdownOptions(masterData.gem_material)}
                classDr="tw-border-0 !tw-bg-white tw-text-base"
              >
                {t('list.filter.gem-material')}
              </Dropdown>

              <Dropdown
                value={state.jewelry_material}
                className="tw-border tw-rounded-5 custom-border-v2 md:tw-w-full tw-py-1 tw-px-2"
                onChange={onJewelryMaterialChange}
                options={buildDropdownOptions(masterData.jewelry_material)}
                classDr="!tw-border-0 !tw-bg-white tw-text-base"
              >
                {t('list.filter.jewelry-material')}
              </Dropdown>

              <Dropdown
                value={state.color}
                onChange={onColorChange}
                className="tw-border tw-rounded-5 custom-border-v2 md:tw-w-full tw-py-1 tw-px-2"
                options={
                  state.cat_code !== 'NGOCTRAI'
                    ? buildDropdownOptions(masterData.color)
                    : buildDropdownOptions(masterDataPearl.color)
                }
                classDr="!tw-border-0 !tw-bg-white tw-text-base"
              >
                {t('list.filter.color')}
              </Dropdown>

              <Dropdown
                value={state.gem_pure}
                className="tw-border tw-rounded-5 custom-border-v2 md:tw-w-full tw-py-1 tw-px-2"
                onChange={onGemPureChange}
                options={buildDropdownOptions(masterData.gem_pure)}
                classDr="!tw-border-0 !tw-bg-white tw-text-base"
              >
                {t('list.filter.gem-pure')}
              </Dropdown>

              <Dropdown
                value={state.gem_type}
                onChange={onGemTypeChange}
                className="tw-border tw-rounded-5 custom-border-v2 md:tw-w-full tw-py-1 tw-px-2"
                options={
                  state.cat_code !== 'NGOCTRAI'
                    ? buildDropdownOptions(masterData.gem_type)
                    : buildDropdownOptions(masterDataPearl.gem_type)
                }
                classDr="tw-border-0 !tw-bg-white tw-text-base"
              >
                {t('list.filter.gem-type')}
              </Dropdown>

              <Dropdown
                value={state.price}
                onChange={onPriceChange}
                className="tw-border tw-rounded-5 custom-border-v2 md:tw-w-full tw-py-1 tw-px-2"
                options={priceFilter}
                classDr="tw-border-0 !tw-bg-white tw-text-base"
              >
                {t('list.filter.price')}
              </Dropdown>
            </div>
          </div>
          <div className="md:tw-col-span-1"></div>
          <div className="md:tw-col-span-3">
            <Dropdown
              value={state.sort_by}
              onChange={onChangeSort}
              className="tw-border tw-rounded-5 custom-border-v2 md:tw-w-full tw-py-1 tw-px-2"
              options={sortByOptions}
              classDr="tw-border-0 !tw-bg-white tw-text-base"
            >
              {t('list.filter.sort-by')}
            </Dropdown>
          </div>
        </div>
      </div>
      <div className="tw-block md:tw-hidden">
        <div className="tw-flex tw-gap-x-3">
          <Input
            onChange={(e) => optimizedFn(e.target.value)}
            // variant={InputVariant.Outlined}
            value={state.product_keyword}
            startAdornment={
              <Icon className="tw-text-secondary-70" icon={<SearchIcon />} />
            }
            placeholder={t('list.filter.keyword')}
            containerClassName="tw-bg-white tw-rounded-5 tw-w-full sm:tw-w-full tw-text-primary-main tw-border tw-border-primary-main tw-py-2 tw-px-3"
            className="tw-text-secondary-70"
          />
          <div
            className="tw-flex tw-flex-col tw-items-center tw-text-primary-main tw-w-10"
            onClick={() => setShow(!isShow)}
          >
            <Icon icon={<FilterIcon />} className="tw-h-7 tw-w-7" />
            <span className="tw-text-10">{t('list.label.filter-mobile')}</span>
          </div>
        </div>

        <AnimatePresence>
          {isShow && (
            <Modal className="!tw-w-full !tw-rounded-none !tw-px-4 !tw-h-full tw-flex tw-flex-col">
              <ModalHeader className="tw-relative">
                <div className="tw-text-center tw-font-medium tw-text-secondary-70 tw-uppercase">
                  {t('list.label.filter-mobile')}
                </div>
                <div
                  className="tw-absolute tw-top-0 tw-right-0"
                  onClick={() => setShow(!isShow)}
                >
                  <Icon
                    className="tw-tw-6 tw-h-6 tw-p-1"
                    icon={<XLineIcon />}
                  ></Icon>
                </div>
              </ModalHeader>
              <ModalBody className="tw-flex tw-flex-col tw-gap-y-6 tw-pt-6 tw-flex-1 tw-h-full">
                <Dropdown
                  value={state.gem_material}
                  onChange={onGemMaterialChange}
                  className="tw-border tw-rounded-5 custom-border-v2 md:tw-w-full tw-py-1 tw-px-2"
                  options={buildDropdownOptions(masterData.gem_material)}
                  classDr="tw-border-0 !tw-bg-white tw-text-base"
                >
                  {t('list.filter.gem-material')}
                </Dropdown>

                <Dropdown
                  value={state.jewelry_material}
                  className="tw-border tw-rounded-5 custom-border-v2 md:tw-w-full tw-py-1 tw-px-2"
                  onChange={onJewelryMaterialChange}
                  options={buildDropdownOptions(masterData.jewelry_material)}
                  classDr="!tw-border-0 !tw-bg-white tw-text-base"
                >
                  {t('list.filter.jewelry-material')}
                </Dropdown>

                <Dropdown
                  value={state.color}
                  onChange={onColorChange}
                  className="tw-border tw-rounded-5 custom-border-v2 md:tw-w-full tw-py-1 tw-px-2"
                  options={
                    state.cat_code !== 'NGOCTRAI'
                      ? buildDropdownOptions(masterData.color)
                      : buildDropdownOptions(masterDataPearl?.color)
                  }
                  classDr="!tw-border-0 !tw-bg-white tw-text-base"
                >
                  {t('list.filter.color')}
                </Dropdown>

                <Dropdown
                  value={state.gem_type}
                  onChange={onGemTypeChange}
                  className="tw-border tw-rounded-5 custom-border-v2 md:tw-w-full tw-py-1 tw-px-2"
                  options={
                    state.cat_code !== 'NGOCTRAI'
                      ? buildDropdownOptions(masterData.gem_type)
                      : buildDropdownOptions(masterDataPearl?.gem_type)
                  }
                  classDr="tw-border-0 !tw-bg-white tw-text-base"
                >
                  {t('list.filter.gem-type')}
                </Dropdown>

                <Dropdown
                  value={state.price}
                  onChange={onPriceChange}
                  className="tw-border tw-rounded-5 custom-border-v2 md:tw-w-full tw-py-1 tw-px-2"
                  options={priceFilter}
                  classDr="tw-border-0 !tw-bg-white tw-text-base"
                >
                  {t('list.filter.price')}
                </Dropdown>

                <Dropdown
                  value={state.sort_by}
                  onChange={onChangeSort}
                  className="tw-border tw-rounded-5 custom-border-v2 md:tw-w-full tw-py-1 tw-px-2"
                  options={sortByOptions}
                  classDr="tw-border-0 !tw-bg-white tw-text-base"
                >
                  {t('list.filter.sort-by')}
                </Dropdown>
              </ModalBody>
              <ModalFooter>
                <Button
                  onClick={() => setShow(!isShow)}
                  className="tw-w-full tw-text-base tw-text-white tw-bg-primary-main !tw-rounded-5"
                >
                  {t('list.label.filter-product')}
                </Button>
              </ModalFooter>
            </Modal>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default FilterPartial;
